"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { VEHICLE_SIZES, type VehicleType } from "~/lib/constants/vehicle";
import { cn } from "~/lib/utils";

/**
 * The one question an event card can't answer from the event.
 *
 * Only ever shown to a signed-in driver with no vehicle on file who hasn't
 * searched from this browser before — the default vehicle and the remembered
 * size are both tried first (see `useOpenEventSearch`). The answer is
 * remembered, so this is at most once per browser, not once per event.
 *
 * Asking beats defaulting to "standard": `vehicle_size` filters which spots
 * come back, so a guess wrong for a truck or a motorcycle hands the driver a
 * list of spots that won't fit their car with nothing on screen to explain it.
 *
 * The pills are built from `VEHICLE_SIZES` rather than by reusing the search
 * page's `VehicleSizePicker`, deliberately: the search flow is not being
 * changed to accommodate events, and that includes not growing a second caller
 * whose needs could later pull it around. The size list itself is shared, which
 * is the part that must not drift.
 */
export function VehicleSizePrompt(t0) {
  const $ = _c(17);
  const {
    open,
    onSelect,
    onDismiss,
    eventName
  } = t0;
  let t1;
  if ($[0] !== onDismiss) {
    t1 = next => !next && onDismiss();
    $[0] = onDismiss;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogTitle className="text-[17px] font-semibold text-body">What are you driving?</DialogTitle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = eventName ? `So we only show spots that fit, near ${eventName}.` : "So we only show spots that fit your car.";
  let t4;
  if ($[3] !== t3) {
    t4 = <DialogHeader className="pr-8">{t2}<DialogDescription className="text-secondary">{t3}</DialogDescription></DialogHeader>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== onSelect) {
    t5 = VEHICLE_SIZES.map(t6 => {
      const {
        value,
        label
      } = t6;
      return <button key={value} type="button" onClick={() => onSelect(value)} className={cn("h-9 rounded-(--r-full) border border-border bg-surface-2 px-3.5 text-[13px] font-medium text-secondary", "transition-all hover:border-(--blue) hover:bg-surface-3 hover:text-body", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--blue)")}>{label}</button>;
    });
    $[5] = onSelect;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <div className="flex flex-wrap gap-2">{t5}</div>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <p className="text-[12px] text-muted">You can change this on the results page at any time.</p>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t4 || $[11] !== t6) {
    t8 = <DialogContent className="gap-5 border border-border bg-surface text-body shadow-(--shadow-xl) sm:max-w-[420px] md:max-w-[420px]">{t4}{t6}{t7}</DialogContent>;
    $[10] = t4;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== open || $[14] !== t1 || $[15] !== t8) {
    t9 = <Dialog open={open} onOpenChange={t1}>{t8}</Dialog>;
    $[13] = open;
    $[14] = t1;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}